import React, { useCallback, useMemo } from 'react';
import {
  QuestionOption,
  QuestionnaireQuestion,
  ValueType,
} from '@core/generated/gql-types';
import MultiSelect, {
  MultiSelectOption,
} from '@/components/shared/MultiSelect';

interface QuestionnaireSectionProps {
  questions: Array<QuestionnaireQuestion | null>;
  responses: Record<string, string>;
  updateResponse: (questionId: string, value: string) => void;
  isLoading?: boolean;
}

const getValidOptions = (options?: Array<QuestionOption | null> | null) => {
  if (!options) {
    return [];
  }

  return options.filter(
    (option): option is QuestionOption =>
      option !== null && !!option.value && !!option.label,
  );
};

const QuestionnaireSection: React.FC<QuestionnaireSectionProps> = ({
  questions,
  responses,
  updateResponse,
  isLoading = false,
}) => {
  const validQuestions = useMemo(() => {
    return questions.filter(
      (question): question is QuestionnaireQuestion => question !== null,
    );
  }, [questions]);

  const renderBooleanQuestion = useCallback(
    (question: QuestionnaireQuestion, value: string) => {
      return (
        <div className="space-y-3">
          <div className="text-sm font-medium text-gray-800">
            {question.questionTitle}
            {question.required ? ' *' : ''}
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name={question.id}
                checked={value === 'true'}
                onChange={() => updateResponse(question.id, 'true')}
              />
              Yes
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name={question.id}
                checked={value === 'false'}
                onChange={() => updateResponse(question.id, 'false')}
              />
              No
            </label>
          </div>
        </div>
      );
    },
    [updateResponse],
  );

  const renderSingleSelectQuestion = useCallback(
    (question: QuestionnaireQuestion, value: string) => {
      const options = getValidOptions(question.options);
      return (
        <div className="space-y-2">
          <label
            htmlFor={question.id}
            className="block text-sm font-medium text-gray-800"
          >
            {question.questionTitle}
            {question.required ? ' *' : ''}
          </label>
          <select
            id={question.id}
            value={value}
            onChange={(event) =>
              updateResponse(question.id, event.target.value)
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          >
            <option value="">Select an option...</option>
            {options.map((option) => (
              <option key={option.value} value={option.value || ''}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    },
    [updateResponse],
  );

  const renderMultiSelectQuestion = useCallback(
    (question: QuestionnaireQuestion, value: string) => {
      const options = getValidOptions(question.options);
      const selectedValues = value ? value.split(',').filter(Boolean) : [];
      const dropdownOptions: MultiSelectOption[] = options.map((option) => ({
        id: option.value,
        name: option.label,
      }));
      const selectedOptions: MultiSelectOption[] = dropdownOptions.filter(
        (option) => option.id && selectedValues.includes(option.id),
      );

      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-800">
            {question.questionTitle}
            {question.required ? ' *' : ''}
          </label>
          <MultiSelect
            options={dropdownOptions}
            value={selectedOptions}
            onChange={(selected) =>
              updateResponse(
                question.id,
                selected
                  .map((option) => option.id)
                  .filter((id): id is string => Boolean(id))
                  .join(','),
              )
            }
            placeholder="Select options..."
          />
        </div>
      );
    },
    [updateResponse],
  );

  const renderInputQuestion = useCallback(
    (
      question: QuestionnaireQuestion,
      value: string,
      inputType: 'text' | 'number',
      placeholder: string,
    ) => {
      return (
        <div className="space-y-2">
          <label
            htmlFor={question.id}
            className="block text-sm font-medium text-gray-800"
          >
            {question.questionTitle}
            {question.required ? ' *' : ''}
          </label>
          <input
            id={question.id}
            type={inputType}
            value={value}
            placeholder={placeholder}
            onChange={(event) =>
              updateResponse(question.id, event.target.value)
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
      );
    },
    [updateResponse],
  );

  const renderQuestion = useCallback(
    (question: QuestionnaireQuestion) => {
      const value = responses[question.id] || '';
      const hasOptions = !!question.options?.length;

      switch (question.answerType) {
        case ValueType.Number:
          return renderInputQuestion(
            question,
            value,
            'number',
            'Enter a number...',
          );
        case ValueType.Boolean:
          return renderBooleanQuestion(question, value);
        case ValueType.String:
          if (hasOptions) {
            return renderSingleSelectQuestion(question, value);
          }
          return renderInputQuestion(
            question,
            value,
            'text',
            'Enter your answer...',
          );
        case ValueType.Array:
          if (hasOptions) {
            return renderMultiSelectQuestion(question, value);
          }
          return renderInputQuestion(
            question,
            value,
            'text',
            'Enter your answer...',
          );
        default:
          if (hasOptions) {
            return renderSingleSelectQuestion(question, value);
          }
          return renderInputQuestion(
            question,
            value,
            'text',
            'Enter your answer...',
          );
      }
    },
    [
      renderBooleanQuestion,
      renderInputQuestion,
      renderMultiSelectQuestion,
      renderSingleSelectQuestion,
      responses,
    ],
  );

  if (validQuestions.length === 0 && !isLoading) {
    return null;
  }

  return (
    <div className="mt-6 mb-6 rounded-lg border border-gray-200 bg-gray-50 p-5">
      <div className="mb-4">
        <div className="text-base font-semibold text-gray-900">
          Job questionnaire
        </div>
        <div className="mt-1 text-sm text-gray-600">
          Please answer the following questions about this job posting.
        </div>
      </div>
      {isLoading ? (
        <div className="text-sm text-gray-500">Loading questionnaire...</div>
      ) : (
        <div className="space-y-4">
          {validQuestions.map((question) => (
            <div key={question.id} className="rounded-md bg-white p-4">
              {renderQuestion(question)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuestionnaireSection;
